import axios from 'axios';
import {API_URL, MAPBOX} from './constants';

// --- Repli TomTom : proxy backend /module/geocode + cache mémoire (durée de vie : session admin) ---
const addressCache = new Map();
const MAX_ADDRESS_CACHE = 200;
const normalizeQuery = (query) => query.trim().replace(/\s+/g, ' ').toLowerCase();

// --- Google Maps : utilisé si le chauffeur a renseigné sa clé (même API que le widget de réservation) ---
// On garde le react-select existant : on appelle l'API Places en programmatique (AutocompleteService
// pour les suggestions, PlacesService.getDetails pour les coordonnées) plutôt que le widget Autocomplete
// qui exige un <input>. Un session token regroupe la frappe + le détail final (tarification par session).
let googleApiKey = null;
let googleLoadPromise = null;
let autocompleteService = null;
let placesService = null;
let sessionToken = null;

const loadGoogleMaps = (apiKey) => {
  if (googleLoadPromise) {
    return googleLoadPromise;
  }
  googleLoadPromise = new Promise((resolve, reject) => {
    if (window.google && window.google.maps && window.google.maps.places) {
      resolve(window.google);
      return;
    }
    const callbackName = '__ccGoogleMapsReady';
    window[callbackName] = () => resolve(window.google);
    const script = document.createElement('script');
    script.src = 'https://maps.googleapis.com/maps/api/js?key=' + encodeURIComponent(apiKey)
      + '&libraries=places&v=weekly&loading=async&callback=' + callbackName;
    script.async = true;
    script.onerror = () => reject(new Error('Google Maps JS API failed to load'));
    document.head.appendChild(script);
  });
  return googleLoadPromise;
};

const ensureGoogleServices = async () => {
  const google = await loadGoogleMaps(googleApiKey);
  if (!autocompleteService) {
    autocompleteService = new google.maps.places.AutocompleteService();
    placesService = new google.maps.places.PlacesService(document.createElement('div'));
  }
  if (!sessionToken) {
    sessionToken = new google.maps.places.AutocompleteSessionToken();
  }
  return google;
};

const googleSearch = async (query) => {
  await ensureGoogleServices();
  return new Promise((resolve) => {
    autocompleteService.getPlacePredictions({input: query, sessionToken}, (predictions, status) => {
      if (status !== window.google.maps.places.PlacesServiceStatus.OK || !predictions) {
        resolve([]);
        return;
      }
      resolve(predictions.map((prediction) => ({
        value: prediction.place_id,
        label: prediction.description,
        position: null,
        placeId: prediction.place_id
      })));
    });
  });
};

const googleResolve = (option) => new Promise((resolve) => {
  placesService.getDetails({placeId: option.placeId, fields: ['geometry'], sessionToken}, (place, status) => {
    // getDetails consomme le session token : on en repart d'un neuf pour la prochaine recherche.
    sessionToken = new window.google.maps.places.AutocompleteSessionToken();
    if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place || !place.geometry) {
      resolve(null);
      return;
    }
    resolve({lat: place.geometry.location.lat(), lon: place.geometry.location.lng()});
  });
});

const tomtomSearch = async (query) => {
  const key = normalizeQuery(query);
  if (addressCache.has(key)) {
    return addressCache.get(key);
  }
  const response = await axios.get(API_URL + '/module/geocode', {params: {q: query.trim()}});
  const results = (response && response.data && response.data.results) || [];
  const options = results.map((item) => ({
    value: item.id,
    label: item.address.freeformAddress,
    position: item.position,
    placeId: null
  }));
  if (addressCache.size >= MAX_ADDRESS_CACHE) {
    addressCache.clear();
  }
  addressCache.set(key, options);
  return options;
};

export default {
  // Active Google Maps pour le géocodage si le chauffeur a renseigné sa clé, sinon repli TomTom.
  configureGoogle: (apiKey) => {
    if (apiKey && apiKey.trim()) {
      googleApiKey = apiKey.trim();
      // Préchargement du SDK ; en cas d'échec on retombe silencieusement sur TomTom.
      loadGoogleMaps(googleApiKey).catch(() => {
        googleApiKey = null;
      });
    } else {
      googleApiKey = null;
    }
  },

  // Renvoie une liste d'options normalisées : [{value, label, position|null, placeId|null}].
  search: async (query) => {
    if (googleApiKey) {
      try {
        return await googleSearch(query);
      } catch (e) {
        // Google indisponible → repli TomTom.
      }
    }
    return tomtomSearch(query);
  },

  // Résout les coordonnées de l'option choisie : {lat, lon} ou null.
  // TomTom fournit déjà la position ; Google nécessite un appel getDetails sur le placeId.
  resolve: async (option) => {
    if (!option) {
      return null;
    }
    if (option.position) {
      return option.position;
    }
    if (option.placeId && googleApiKey) {
      return await googleResolve(option);
    }
    return null;
  },

  route: async (startingLng, startingLat, arrivalLng, arrivalLat) => {
    return await axios.get('https://api.mapbox.com/directions/v5/mapbox/driving/' + startingLng + '%2C' + startingLat + '%3B' + arrivalLng + '%2C' + arrivalLat, {
      params: {
        alternatives: false,
        geometries: 'geojson',
        overview: 'simplified',
        steps: false,
        access_token: MAPBOX,
      },
    });
  }
}
